// LeadPipelineSummaryWidget — compact pipeline-stage summary for advice
// firms. Sources from `/api/analytics/pipeline-funnel` (counts, values,
// probability per stage) and `/api/leads` for the "Won this month" tally.
// Renders null for non-advice orgs.

import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Target, TrendingUp, ArrowRight, Loader2, Trophy,
} from 'lucide-react';

const STAGE_LABELS = {
  new: 'New',
  qualifying: 'Qualifying',
  proposal: 'Proposal',
  negotiating: 'Negotiating',
  won: 'Won',
  lost: 'Lost',
};

const PIPELINE_STAGES = ['new', 'qualifying', 'proposal', 'negotiating'];

function formatCurrency(n) {
  if (!n) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n}`;
}

export default function LeadPipelineSummaryWidget({ ctx }) {
  const { token } = useAuth();
  const [funnel, setFunnel] = useState(null);
  const [wonThisMonth, setWonThisMonth] = useState({ count: 0, value: 0 });
  const [loading, setLoading] = useState(true);

  const isAdvice = ctx?.orgType === 'advice';

  useEffect(() => {
    if (!token || !isAdvice) { setLoading(false); return; }
    (async () => {
      try {
        const [funnelRes, leadsRes] = await Promise.all([
          api.get(token, '/analytics/pipeline-funnel'),
          api.get(token, '/leads'),
        ]);
        setFunnel(funnelRes.data);
        // Compute "Won this month" from the leads list
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const wonLeads = (leadsRes.data || []).filter((l) => {
          if (l.stage !== 'won') return false;
          const dateStr = l.wonAt || l.updatedAt || l.closedAt;
          if (!dateStr) return false;
          return new Date(dateStr) >= startOfMonth;
        });
        setWonThisMonth({
          count: wonLeads.length,
          value: wonLeads.reduce((acc, l) => acc + (Number(l.value) || 0), 0),
        });
      } catch (e) {
        console.error('LeadPipelineSummary fetch failed', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, isAdvice]);

  if (!isAdvice) return null;

  if (loading) {
    return (
      <div className="rounded-xl border border-[#E6E0D9] bg-white p-5 flex items-center justify-center" data-testid="lead-pipeline-widget-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#4A3B32]" />
      </div>
    );
  }

  const stages = funnel?.stages || [];
  const pipelineStages = stages.filter((s) => PIPELINE_STAGES.includes(s.stage));
  const totalValue = pipelineStages.reduce((acc, s) => acc + (s.totalValue || 0), 0);
  const weightedValue = pipelineStages.reduce(
    (acc, s) => acc + (s.totalValue || 0) * (s.probability || 0),
    0,
  );
  const totalCount = pipelineStages.reduce((acc, s) => acc + (s.count || 0), 0);
  const wonStage = stages.find((s) => s.stage === 'won') || { count: 0 };
  const lostStage = stages.find((s) => s.stage === 'lost') || { count: 0 };
  const totalClosed = (wonStage.count || 0) + (lostStage.count || 0);
  const winRate = totalClosed > 0 ? Math.round((wonStage.count / totalClosed) * 100) : null;

  return (
    <div className="rounded-xl border border-[#E6E0D9] bg-white p-5" data-testid="lead-pipeline-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-emerald-600" />
          <h3 className="font-heading text-base text-[#2C2420]">Lead pipeline</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => ctx?.setActiveTab?.('leads')}
          className="h-7 text-xs text-[#A86A3A]"
          data-testid="lead-pipeline-open-btn"
        >
          Open pipeline <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>

      {/* Top metrics row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="lead-pipeline-metrics">
        <Metric label="In pipeline"   value={totalCount} testId="lp-metric-count" />
        <Metric label="Pipeline value" value={formatCurrency(totalValue)} testId="lp-metric-value" />
        <Metric label="Weighted"       value={formatCurrency(Math.round(weightedValue))} testId="lp-metric-weighted" />
        <Metric label="Win rate"       value={winRate === null ? '—' : `${winRate}%`} testId="lp-metric-winrate" />
      </div>

      {/* Stages */}
      <div className="space-y-2" data-testid="lead-pipeline-stages">
        {pipelineStages.map((s) => {
          const pct = totalCount > 0 ? Math.round((s.count / totalCount) * 100) : 0;
          return (
            <button
              key={s.stage}
              onClick={() => ctx?.setActiveTab?.('leads')}
              className="w-full flex items-center gap-3 text-left group"
              data-testid={`lp-stage-${s.stage}`}
            >
              <span className="text-xs text-[#5D524D] w-24 shrink-0 group-hover:text-[#2C2420]">{STAGE_LABELS[s.stage] || s.stage}</span>
              <div className="flex-1 h-2 bg-[#F5F3F1] rounded-full overflow-hidden">
                <div className={cn('h-full bg-emerald-500 rounded-full transition-all duration-500')} style={{ width: `${pct}%` }} />
              </div>
              <span className="text-xs font-medium text-[#2C2420] w-8 text-right">{s.count}</span>
              <span className="text-xs text-[#8C8078] w-16 text-right hidden sm:inline">{formatCurrency(s.totalValue)}</span>
            </button>
          );
        })}
      </div>

      {/* Won this month */}
      <div className="mt-4 pt-4 border-t border-[#F0EAE3] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" />
          <span className="text-sm text-[#2C2420]">
            <strong className="font-medium">{wonThisMonth.count}</strong> won this month
            {wonThisMonth.value > 0 && <span className="text-[#8C8078]"> · {formatCurrency(wonThisMonth.value)}</span>}
          </span>
        </div>
        {winRate !== null && (
          <div className="flex items-center gap-1.5 text-xs text-[#5D524D]">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
            All-time {winRate}% win rate
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, testId }) {
  return (
    <div className="rounded-lg border border-[#F0EAE3] bg-[#FCFAF7] p-2.5" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wide text-[#8C8078] mb-1">{label}</p>
      <p className="text-lg font-heading text-[#2C2420] leading-none">{value}</p>
    </div>
  );
}
